import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { ExternalLink } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

const ResourceCard = ({ name, url, description }) => (
  <Card className="feature-card resource-card">
    <CardContent className="feature-content">
      <h3 className="feature-title resource-name">
        <a href={url} target="_blank" rel="noopener noreferrer" className="resource-link">
          {name}
          <ExternalLink className="w-4 h-4 inline-block ml-2" />
        </a>
      </h3>
      <p className="feature-description">{description}</p>
    </CardContent>
  </Card>
);

export const ResourcesPage = () => {
  return (
    <div className="landing-page" data-testid="resources-page">
      <SiteHeader />

      <section className="page-content-section">
        <div className="container page-container-inner">
          <h1 className="page-main-title" data-testid="resources-title">Parent Resources</h1>
          <p className="page-subtitle">
            BuckTime! gives your child a daily pot and lets them choose how to spend it. These resources go deeper — helping families build healthy screen habits, talk about money, and understand the tech shaping childhood.
          </p>

          <div className="resource-categories">
            <div className="resource-category" data-testid="category-money">
              <h2 className="resource-category-title">Financial Literacy for Kids</h2>
              <div className="resource-grid">
                <ResourceCard
                  name="Money as You Grow"
                  url="https://www.consumerfinance.gov/consumer-tools/money-as-you-grow/"
                  description="From the U.S. Consumer Financial Protection Bureau. Age-by-age milestones for teaching kids about saving, spending, and earning — the same three jars BuckTime! uses."
                />
                <ResourceCard
                  name="Canadian Foundation for Economic Education"
                  url="https://cfee.org"
                  description="Canadian-focused resources for teaching kids and teens about money, entrepreneurship, and financial decision-making. Their Talk With Our Kids About Money program is free."
                />
                <ResourceCard
                  name="Practical Money Skills"
                  url="https://www.practicalmoneyskills.com"
                  description="Free games, lesson plans, and tools that make money concepts tangible for kids. Great companion to the daily choices your child is already making in BuckTime!."
                />
              </div>
            </div>

            <div className="resource-category" data-testid="category-safety">
              <h2 className="resource-category-title">Digital Safety &amp; Parenting</h2>
              <div className="resource-grid">
                <ResourceCard
                  name="Common Sense Media"
                  url="https://www.commonsensemedia.org"
                  description="Age-based ratings for movies, games, and apps. Their research on screen time and child development is the gold standard for families navigating tech."
                />
                <ResourceCard
                  name="Family Online Safety Institute (FOSI)"
                  url="https://www.fosi.org"
                  description="Their approach mirrors BuckTime! — mentoring over monitoring. Their 7 Steps to Good Digital Parenting framework helps parents guide rather than gatekeep."
                />
                <ResourceCard
                  name="Wait Until 8th"
                  url="https://www.waituntil8th.org"
                  description="A grassroots movement encouraging parents to delay smartphones until at least 8th grade. A supportive community for families making intentional tech choices."
                />
              </div>
            </div>

            <div className="resource-category" data-testid="category-psychology">
              <h2 className="resource-category-title">Psychology &amp; Wellness</h2>
              <div className="resource-grid">
                <ResourceCard
                  name="The Center for Humane Technology"
                  url="https://www.humanetech.com"
                  description="Founded by the team behind The Social Dilemma. They explain the persuasive design tricks apps use — the same ones BuckTime! makes visible by putting a dollar value on each minute."
                />
                <ResourceCard
                  name="Digital Wellness Lab"
                  url="https://digitalwellnesslab.org"
                  description="Evidence-based research from Boston Children's Hospital on how screen time affects physical and mental health. Helpful context for setting your child's daily pot."
                />
                <ResourceCard
                  name="Screenagers"
                  url="https://www.screenagersmovie.com"
                  description="Their Tech Talk Tuesdays blog gives parents weekly conversation starters about technology — designed to talk with kids, not at them."
                />
              </div>
            </div>

            <div className="resource-category" data-testid="category-tools">
              <h2 className="resource-category-title">Practical Tools &amp; Communities</h2>
              <div className="resource-grid">
                <ResourceCard
                  name="Protect Young Minds"
                  url="https://www.protectyoungminds.org"
                  description="Practical how-to guides for setting up filters on specific devices — consoles, routers, tablets. Pairs well with BuckTime! for a layered approach."
                />
                <ResourceCard
                  name="Fairplay (formerly CCFC)"
                  url="https://fairplayforkids.org"
                  description="Advocates for ending manipulative marketing aimed at children. Their Screen-Free Week resources are great for families experimenting with a digital reset."
                />
                <ResourceCard
                  name="Internet Matters"
                  url="https://www.internetmatters.org"
                  description="Step-by-step guides for setting up parental controls across every device imaginable — phones, consoles, tablets, even smart TVs."
                />
                <ResourceCard
                  name="Smart Social"
                  url="https://smartsocial.com"
                  description="Focused on social media specifically. Helps parents understand trending apps and their risks before kids download them."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default ResourcesPage;
